import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import MobileQuickNav from "../components/layout/MobileQuickNav";
import { podcastsApi } from "../api/podcastsApi";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import { usePodcastPlayer } from "../podcasts/playerStore";
import { isAegisEmbedUrl, resolveAegisPlayerUrl } from "../podcasts/aegisPlayer";
import { useSubscriptionLimitsGate } from "../subscription/useSubscriptionLimitsGate";
import { useToast } from "../ui/toast/ToastProvider";
import { getSiteOrigin, joinUrl } from "../utils/seo";

const PODCASTS_PAGE_SIZE = 50;
const EPISODES_PAGE_SIZE = 30;

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function safeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function unwrapPagePayload(payload) {
  const raw = asObject(payload);
  return raw.item ?? raw.items ?? raw.data ?? raw;
}

function normalizePage(payload) {
  const unwrapped = unwrapPagePayload(payload);
  if (Array.isArray(unwrapped)) {
    return {
      content: unwrapped,
      number: 0,
      size: unwrapped.length,
      totalElements: unwrapped.length,
      totalPages: 1,
    };
  }

  const page = asObject(unwrapped);
  const content = asArray(page.content || page.items || page.data || page.records);
  return {
    content,
    number: toNumber(page.number ?? page.page, 0),
    size: toNumber(page.size ?? page.pageSize, content.length || 0),
    totalElements: toNumber(page.totalElements ?? page.total ?? page.count, content.length),
    totalPages: Math.max(1, toNumber(page.totalPages, content.length ? 1 : 0)),
  };
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDuration(seconds) {
  const sec = Number(seconds);
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  const minutes = Math.floor(sec / 60);
  const rest = sec % 60;
  if (rest === 0) return `${minutes} мин`;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

function formatNumber(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, toNumber(value, 0)));
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = safeText(value);
    if (text) return text;
  }
  return "";
}

function firstNumeric(...values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
}

function normalizePodcast(item = {}) {
  const raw = asObject(item);
  const topics = asArray(raw.topics || raw.topicLabels);
  const topic = firstNonEmpty(raw.topic, topics[0], "Без темы");
  const published =
    raw.published !== undefined
      ? Boolean(raw.published)
      : raw.isPublished !== undefined
        ? Boolean(raw.isPublished)
        : raw.status
          ? String(raw.status).toUpperCase() === "PUBLISHED"
          : true;

  return {
    id: raw.id ?? raw.podcastId ?? null,
    slug: firstNonEmpty(raw.slug),
    title: firstNonEmpty(raw.title, raw.name, "Подкаст"),
    shortDescription: firstNonEmpty(raw.shortDescription, raw.description, "Описание подкаста пока не заполнено."),
    description: firstNonEmpty(raw.description, raw.shortDescription, "Описание подкаста пока не заполнено."),
    topic,
    topics,
    coverEmoji: firstNonEmpty(raw.coverEmoji, raw.emoji, "🎙️"),
    coverImageUrl: firstNonEmpty(raw.coverImageUrl, raw.imageUrl, raw.coverUrl, raw.artworkUrl),
    episodesCount: Math.max(
      0,
      toNumber(firstNumeric(raw.episodesCount, raw.totalEpisodes, raw.episodesTotal), 0)
    ),
    listensCount: Math.max(
      0,
      toNumber(firstNumeric(raw.totalListens, raw.listensCount, raw.playsCount), 0)
    ),
    subscribersCount: Math.max(
      0,
      toNumber(firstNumeric(raw.subscribersCount, raw.subscriptionsCount, raw.followersCount), 0)
    ),
    published,
    statusLabel: published ? "Опубликован" : "Черновик",
    rating: firstNumeric(raw.ratingAvg, raw.rating, raw.avgRating),
    ratingsCount: firstNumeric(raw.ratingsCount, raw.ratingVotes, raw.votesCount),
    updatedAt: raw.updatedAt || raw.modifiedAt || raw.lastModified || raw.createdAt || null,
  };
}

function normalizeEpisode(item = {}, fallbackPodcast = {}) {
  const raw = asObject(item);
  const publishedAt = raw.publishedAt || raw.publishDate || raw.createdAt || null;
  const durationSeconds = firstNumeric(
    raw.durationSeconds,
    raw.lengthSeconds,
    raw.durationMinutes ? Number(raw.durationMinutes) * 60 : undefined,
    raw.durationMin ? Number(raw.durationMin) * 60 : undefined
  );

  return {
    id: raw.id ?? raw.episodeId ?? null,
    podcastId: raw.podcastId ?? fallbackPodcast.id ?? null,
    podcastSlug: firstNonEmpty(raw.podcastSlug, fallbackPodcast.slug),
    podcastTitle: firstNonEmpty(raw.podcastTitle, fallbackPodcast.title, "Подкаст"),
    title: firstNonEmpty(raw.title, "Эпизод"),
    coverEmoji: firstNonEmpty(raw.coverEmoji, raw.podcastCoverEmoji, fallbackPodcast.coverEmoji, "🎙️"),
    coverImageUrl: firstNonEmpty(
      raw.coverImageUrl,
      raw.imageUrl,
      raw.coverUrl,
      raw.podcastCoverImageUrl,
      raw.podcastImageUrl,
      fallbackPodcast.coverImageUrl
    ),
    durationSeconds: Math.max(0, toNumber(durationSeconds, 0)),
    listensCount: Math.max(0, toNumber(firstNumeric(raw.listensCount, raw.totalListens), 0)),
    playbackUrl: firstNonEmpty(raw.playbackUrl, raw.audioUrl, raw.streamUrl, raw.mediaUrl),
    aegisAssetId: firstNonEmpty(raw.aegisAssetId, raw.assetId, raw.aegis_asset_id, raw.asset_id),
    aegisProjectId: firstNonEmpty(raw.aegisProjectId, raw.projectId, raw.aegis_project_id, raw.project_id),
    aegisPlayerUrl: resolveAegisPlayerUrl(raw),
    isAegisEmbed:
      Boolean(resolveAegisPlayerUrl(raw)) ||
      isAegisEmbedUrl(raw.playbackUrl || raw.audioUrl || raw.streamUrl || raw.mediaUrl || ""),
    publishedAt,
    publishedLabel: formatDate(publishedAt),
  };
}

function resolveStats(stats = {}, podcasts = []) {
  const source = asObject(stats);
  const totalPodcasts = Math.max(
    0,
    toNumber(firstNumeric(source.totalPodcasts, source.podcastsCount), podcasts.length)
  );
  const publishedPodcasts = Math.max(
    0,
    toNumber(
      firstNumeric(source.publishedPodcasts, source.publishedCount),
      podcasts.filter((item) => item.published).length
    )
  );
  const episodesCount = Math.max(
    0,
    toNumber(
      firstNumeric(source.episodesCount, source.totalEpisodes),
      podcasts.reduce((sum, item) => sum + toNumber(item.episodesCount, 0), 0)
    )
  );
  const listensCount = Math.max(
    0,
    toNumber(
      firstNumeric(source.totalListens, source.listensCount),
      podcasts.reduce((sum, item) => sum + toNumber(item.listensCount, 0), 0)
    )
  );
  return {
    totalPodcasts,
    publishedPodcasts,
    episodesCount,
    listensCount,
  };
}

export default function MyPodcasts() {
  const navigate = useNavigate();
  const toast = useToast();
  const { ensureWithinLimit, limitModal } = useSubscriptionLimitsGate();
  const { me, role, logout } = useAuth();
  const { unreadNotificationsCount } = useNotifications();
  const player = usePodcastPlayer();

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [podcasts, setPodcasts] = React.useState([]);
  const [stats, setStats] = React.useState({});
  const [selectedPodcastId, setSelectedPodcastId] = React.useState(null);
  const [episodesLoading, setEpisodesLoading] = React.useState(false);
  const [episodesError, setEpisodesError] = React.useState("");
  const [episodes, setEpisodes] = React.useState([]);

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => joinUrl(origin, "/dashboard/podcasts"), [origin]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      podcastsApi.getMyPodcasts({ page: 0, size: PODCASTS_PAGE_SIZE }),
      podcastsApi.getMyPodcastStats().catch(() => ({})),
    ])
      .then(([podcastsPayload, statsPayload]) => {
        if (cancelled) return;
        const page = normalizePage(podcastsPayload);
        const normalized = page.content.map(normalizePodcast).filter((item) => item.id || item.slug || item.title);
        setPodcasts(normalized);
        setStats(asObject(statsPayload));
        setSelectedPodcastId((prev) => {
          if (prev && normalized.some((item) => String(item.id) === String(prev))) return prev;
          return normalized[0]?.id ?? null;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Не удалось загрузить мои подкасты");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedPodcast = React.useMemo(
    () => podcasts.find((item) => String(item.id) === String(selectedPodcastId)) || null,
    [podcasts, selectedPodcastId]
  );

  React.useEffect(() => {
    if (!selectedPodcast?.id) {
      setEpisodes([]);
      setEpisodesError("");
      return;
    }

    let cancelled = false;
    setEpisodesLoading(true);
    setEpisodesError("");

    podcastsApi
      .getMyPodcastEpisodes(selectedPodcast.id, { page: 0, size: EPISODES_PAGE_SIZE })
      .then((payload) => {
        if (cancelled) return;
        const page = normalizePage(payload);
        setEpisodes(page.content.map((item) => normalizeEpisode(item, selectedPodcast)));
      })
      .catch((err) => {
        if (cancelled) return;
        setEpisodes([]);
        setEpisodesError(err?.message || "Не удалось загрузить эпизоды");
      })
      .finally(() => {
        if (!cancelled) setEpisodesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPodcast]);

  const filteredPodcasts = React.useMemo(() => {
    const query = safeText(searchInput).toLowerCase();
    if (!query) return podcasts;
    return podcasts.filter((item) => {
      const haystack = `${item.title} ${item.topic} ${item.shortDescription}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [podcasts, searchInput]);

  const resolvedStats = React.useMemo(() => resolveStats(stats, podcasts), [stats, podcasts]);

  const openPodcastPublic = (podcast) => {
    const rid = podcast?.slug || podcast?.id;
    if (!rid) return;
    navigate(`/podcasts/${encodeURIComponent(String(rid))}`);
  };

  const openEpisodePage = (episode) => {
    if (!episode?.id) return;
    navigate(`/podcasts/episodes/${encodeURIComponent(String(episode.id))}`);
  };

  const openPodcastCreatePage = React.useCallback(async () => {
    try {
      const listRaw = await podcastsApi.getMyPodcasts({ page: 0, size: 1 });
      const page = normalizePage(listRaw);
      const usage = Number(page.totalElements) || page.content.length || 0;
      const allowed = ensureWithinLimit({ featureKey: "podcasts", usage });
      if (!allowed) return;
      navigate("/dashboard/podcasts/new");
    } catch (error) {
      toast.error(error?.message || "Не удалось проверить лимит подписки");
    }
  }, [ensureWithinLimit, navigate, toast]);

  const openPodcastEditor = (podcast = null) => {
    if (!podcast?.id) {
      openPodcastCreatePage();
      return;
    }
    navigate(`/dashboard/podcasts/${encodeURIComponent(String(podcast.id))}/edit`, {
      state: { sourcePodcast: podcast },
    });
  };

  const openEpisodeBuilder = (podcast = selectedPodcast, episode = null) => {
    const targetPodcastId = podcast?.id || selectedPodcast?.id || "";
    if (!targetPodcastId) {
      toast.info("Сначала создайте или выберите подкаст, потом добавьте выпуск");
      return;
    }

    const path = episode?.id
      ? `/dashboard/podcasts/${encodeURIComponent(String(targetPodcastId))}/episodes/${encodeURIComponent(String(episode.id))}/edit`
      : `/dashboard/podcasts/${encodeURIComponent(String(targetPodcastId))}/episodes/new`;

    navigate(path, {
      state: {
        podcastId: targetPodcastId,
        sourceEpisode: episode || undefined,
        sourceEpisodeId: episode?.id || undefined,
      },
    });
  };

  const playEpisodeInline = async (episode) => {
    if (!episode) return;
    const playbackQueue = Array.isArray(episodes) && episodes.length ? episodes : [episode];
    const queueStartIndex = playbackQueue.findIndex((item) => String(item?.id ?? "") === String(episode?.id ?? ""));
    const ok = await player.playTrack(
      {
        id: episode.id,
        episodeId: episode.id,
        podcastId: episode.podcastId,
        podcastSlug: episode.podcastSlug,
        title: episode.title,
        podcastTitle: episode.podcastTitle,
        coverImageUrl: episode.coverImageUrl,
        durationSeconds: episode.durationSeconds,
        playbackUrl: episode.playbackUrl,
        assetId: episode.aegisAssetId,
        projectId: episode.aegisProjectId,
        isAegisEmbed: episode.isAegisEmbed,
      },
      {
        autoplay: true,
        queue: playbackQueue,
        queueStartIndex: queueStartIndex >= 0 ? queueStartIndex : 0,
      }
    );
    if (!ok) {
      openEpisodePage(episode);
      return;
    }
    if (episode.id) podcastsApi.registerListen(episode.id).catch(() => {});
  };

  const podcastQuickNavItems = React.useMemo(() => [
    { to: "/podcasts", label: "Каталог", icon: "▶" },
    { to: "/dashboard/podcasts", label: "Мои", icon: "▦" },
    { to: "/dashboard/podcasts/new", label: "Создать", icon: "+", accent: "create" },
  ], []);

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>Мои подкасты | Бюро Души</title>
        <meta
          name="description"
          content="Управление подкастами: список шоу, эпизоды, быстрый переход к публикации и прослушиванию."
        />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={role || "CLIENT"}
          notificationsBadge={unreadNotificationsCount}
          onCloseMobile={() => setSidebarOpen(false)}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="podcasts-topbar__search">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M13 13l4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Найти среди моих подкастов..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>
            </div>
            <div className="podcasts-topbar__right">
              <button className="podcasts-btn podcasts-btn--subtle" type="button" onClick={() => navigate("/podcasts")}>
                Каталог
              </button>
              <button
                className="podcasts-btn podcasts-btn--mint"
                type="button"
                onClick={() => openPodcastEditor()}
              >
                Новый подкаст
              </button>
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {initials(me?.name || me?.login)}
              </button>
            </div>
          </header>

          <main className="podcasts-content">
            <div className="podcasts-management__head">
              <div>
                <h1 className="podcasts-management__title">Мои подкасты</h1>
                <p className="podcasts-management__sub">
                  Управляйте шоу: сначала создайте подкаст, затем внутри него добавляйте эпизоды.
                </p>
              </div>
              <div className="podcasts-management__actions">
                <Link className="podcasts-management__btn" to="/podcasts">
                  Открыть каталог
                </Link>
                <button
                  type="button"
                  className="podcasts-management__btn podcasts-management__btn--mint"
                  onClick={() => openPodcastEditor()}
                >
                  Создать подкаст
                </button>
              </div>
            </div>

            <div className="stats-row">
              <article className="stat-card">
                <div className="stat-val">{formatNumber(resolvedStats.totalPodcasts)}</div>
                <div className="stat-lbl">Всего подкастов</div>
              </article>
              <article className="stat-card">
                <div className="stat-val mint">{formatNumber(resolvedStats.publishedPodcasts)}</div>
                <div className="stat-lbl">Опубликовано</div>
              </article>
              <article className="stat-card">
                <div className="stat-val lav">{formatNumber(resolvedStats.episodesCount)}</div>
                <div className="stat-lbl">Всего эпизодов</div>
              </article>
              <article className="stat-card">
                <div className="stat-val">{formatNumber(resolvedStats.listensCount)}</div>
                <div className="stat-lbl">Прослушиваний</div>
              </article>
            </div>

            {loading ? <div className="podcasts-empty">Загрузка подкастов…</div> : null}
            {!loading && error ? <div className="podcasts-empty">{error}</div> : null}

            {!loading && !error && selectedPodcast ? (
              <section className="podcasts-management__selected">
                <div className="episodes-header">
                  <div>
                    <div className="podcasts-section-label">Текущий подкаст</div>
                    <h2 className="podcasts-management__selected-title">{selectedPodcast.title}</h2>
                    <p className="podcasts-management__selected-sub">{selectedPodcast.shortDescription}</p>
                  </div>
                  <div className="podcasts-management__actions">
                    <select
                      className="podcasts-management__select"
                      value={selectedPodcast.id || ""}
                      onChange={(event) => setSelectedPodcastId(event.target.value)}
                      aria-label="Выбрать подкаст"
                    >
                      {podcasts.map((podcast) => (
                        <option key={`select-${podcast.id || podcast.slug}`} value={podcast.id || ""}>
                          {podcast.title}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="podcasts-management__btn"
                      onClick={() => openPodcastEditor(selectedPodcast)}
                    >
                      Редактировать подкаст
                    </button>
                    <button
                      type="button"
                      className="podcasts-management__btn podcasts-management__btn--mint"
                      onClick={() => openEpisodeBuilder(selectedPodcast)}
                    >
                      + Новый эпизод
                    </button>
                  </div>
                </div>

                {episodesLoading ? <div className="podcasts-empty podcasts-empty--inline">Загрузка эпизодов…</div> : null}
                {!episodesLoading && episodesError ? <div className="podcasts-empty podcasts-empty--inline">{episodesError}</div> : null}

                {!episodesLoading && !episodesError ? (
                  <section className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Эпизод</th>
                          <th>Дата</th>
                          <th>Длительность</th>
                          <th>Прослушивания</th>
                          <th style={{ textAlign: "right" }}>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {episodes.length === 0 ? (
                          <tr>
                            <td colSpan={5}>Эпизоды пока не добавлены</td>
                          </tr>
                        ) : (
                          episodes.map((episode) => (
                            <tr key={`episode-${episode.id || episode.title}`}>
                              <td>
                                <div className="podcasts-management__podcast-cell">
                                  <div className="podcasts-management__podcast-cover">
                                    {episode.coverImageUrl ? (
                                      <img src={episode.coverImageUrl} alt={episode.title} />
                                    ) : (
                                      <span>{episode.coverEmoji}</span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="podcasts-management__podcast-name">{episode.title}</div>
                                    <div className="podcasts-management__podcast-topic">{episode.podcastTitle}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{episode.publishedLabel}</td>
                              <td>{formatDuration(episode.durationSeconds)}</td>
                              <td>{formatNumber(episode.listensCount)}</td>
                              <td>
                                <div className="podcasts-management__actions">
                                  <button
                                    type="button"
                                    className="podcasts-management__btn"
                                    onClick={() => openEpisodePage(episode)}
                                  >
                                    Открыть
                                  </button>
                                  <button
                                    type="button"
                                    className="podcasts-management__btn"
                                    onClick={() => openEpisodeBuilder(selectedPodcast, episode)}
                                  >
                                    Изменить
                                  </button>
                                  <button
                                    type="button"
                                    className="podcasts-management__btn podcasts-management__btn--mint"
                                    onClick={() => playEpisodeInline(episode)}
                                  >
                                    Слушать
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </section>
                ) : null}
              </section>
            ) : null}

            {!loading && !error && !selectedPodcast ? (
              <div className="podcasts-empty">
                Подкасты пока не найдены. Создайте первый подкаст, затем добавьте в него эпизоды.
              </div>
            ) : null}

            {!loading && !error ? (
              <section className="table-wrap">
                <div className="episodes-header podcasts-management__table-head">
                  <div className="podcasts-section-label">Все мои подкасты</div>
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Подкаст</th>
                      <th>Статус</th>
                      <th>Эпизоды</th>
                      <th>Прослушивания</th>
                      <th>Обновлён</th>
                      <th style={{ textAlign: "right" }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPodcasts.length === 0 ? (
                      <tr>
                        <td colSpan={6}>Подкасты пока не найдены</td>
                      </tr>
                    ) : (
                      filteredPodcasts.map((podcast) => (
                        <tr key={`my-podcast-${podcast.id || podcast.slug || podcast.title}`}>
                          <td>
                            <div className="podcasts-management__podcast-cell">
                              <div className="podcasts-management__podcast-cover">
                                {podcast.coverImageUrl ? (
                                  <img src={podcast.coverImageUrl} alt={podcast.title} />
                                ) : (
                                  <span>{podcast.coverEmoji}</span>
                                )}
                              </div>
                              <div>
                                <div className="podcasts-management__podcast-name">{podcast.title}</div>
                                <div className="podcasts-management__podcast-topic">{podcast.topic}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`status-dot ${podcast.published ? "" : "status-draft"}`}>
                              {podcast.statusLabel}
                            </span>
                          </td>
                          <td>{formatNumber(podcast.episodesCount)}</td>
                          <td>{formatNumber(podcast.listensCount)}</td>
                          <td>{formatDate(podcast.updatedAt)}</td>
                          <td>
                            <div className="podcasts-management__actions">
                              <button
                                type="button"
                                className="podcasts-management__btn"
                                onClick={() => openPodcastEditor(podcast)}
                              >
                                Редактировать
                              </button>
                              <button
                                type="button"
                                className="podcasts-management__btn"
                                onClick={() => openPodcastPublic(podcast)}
                              >
                                Открыть
                              </button>
                              <button
                                type="button"
                                className="podcasts-management__btn podcasts-management__btn--mint"
                                onClick={() => openEpisodeBuilder(podcast)}
                              >
                                Добавить эпизод
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </section>
            ) : null}
          </main>
          <MobileQuickNav items={podcastQuickNavItems} />
        </div>
      </div>
      {limitModal}
    </div>
  );
}
